import { useTranslation } from 'react-i18next'
import Badge from '../../../components/ui/Badge'
import Button from '../../../components/ui/Button'
import type { ActionStatusOption } from '../../actions/types'
import GroupActionStats from './GroupActionStats'
import { useGrants } from '../../../auth/useCurrentUser'
import { GRANTS } from '../../../auth/grants'
import type { MeetingGroup } from '../types'

interface Props {
  group: MeetingGroup
  /** Aksiyon özeti göstergesindeki durumlar (REFERANS'tan; boşsa özet bloğu çizilmez). */
  actionStatuses?: ActionStatusOption[]
  busy?: boolean
  /**
   * Aksiyon butonları (düzenle/sil) yalnız yönetim ekranında gösterilir. Toplantılar (salt-okuma
   * drill-down) ekranında CRUD YOKTUR → verilmezse buton çıkmaz. CRUD, "Toplantı Grupları" ekranındadır.
   */
  canManage?: boolean
  onOpen: (group: MeetingGroup) => void
  onEdit?: (group: MeetingGroup) => void
  onDelete?: (group: MeetingGroup) => void
}

/**
 * Toplantı grubu kartı (038; 039 mockup'ına göre yenilendi): başlık satırında ad + <b>grup numarası</b>
 * (analiz 12; ör. {@code TG-2026-0001}) + toplantı sayısı rozeti,
 * altında açıklama, kartın altında aksiyon özeti (toplam + durum dağılımı çubuğu + gösterge).
 * Karta tıklama drill-down (o grubun toplantıları); aksiyon butonları yayılımı durdurur (drill-down tetiklemez).
 */
export default function MeetingGroupCard({
  group,
  actionStatuses,
  busy,
  canManage,
  onOpen,
  onEdit,
  onDelete,
}: Readonly<Props>) {
  const { t } = useTranslation()
  // 077 — Grup No (kart görünümü) de tablo kolonuyla aynı yetkiye bağlı: yalnız RECORD_NUMBER_VIEW (Özel Kalem).
  const showRecordNumber = useGrants().hasGrant(GRANTS.RECORD_NUMBER_VIEW)
  return (
    // 15.09.2026 — **overlay-link**: kart kökü handler'sız, drill-down'ı başlıktaki gerçek `<button>`
    // açıyor ve kartı `::after` ile kaplıyor (`ui.css` → `.ui-card--linked`/`.ui-card-link`; kartın
    // kendi görünümü `.mg-card`ta kalır, iki yardımcı sınıf yalnız konum/kaplama getirir). Düzenle ve
    // Sil düğmeleri o kontrolün KARDEŞİdir. Önceki biçimde kart `role="button"` taşıyor, iki düğme
    // onun İÇİNDE kalıyordu; ARIA'da interaktif kontrol başka bir interaktif kontrol içeremez (THY
    // inceleme bulgusu, 15.09.2026; anayasa → kart kuralı). `e.target === e.currentTarget` elemesi ve
    // düğmelerdeki `stopPropagation` yalnız DAVRANIŞI düzeltiyordu, semantiği değil — kardeşlikle
    // ikisi de gereksizleşti.
    <div className="mg-card ui-card--linked">
      <div className="mg-card-head">
        {/* Numara adın hemen altında (analiz 12): TG-<yıl>-<sıra>. Boşsa hiç çizilmez (yer tutucu yok). */}
        <div className="mg-card-heading">
          {/* Düğme `<h3>`ün İÇİNDE, dışında değil: `<button>`ın içerik modeli phrasing content'tir,
              başlık elemanı oraya konulamaz. Bu sıra hem geçerli HTML hem korunan başlık semantiği. */}
          <h3 className="mg-card-title">
            <button type="button" className="ui-card-link" onClick={() => onOpen(group)}>
              {group.name}
            </button>
          </h3>
          {showRecordNumber && group.recordNumber && (
            <span className="mg-card-number">{group.recordNumber}</span>
          )}
        </div>
        <span className="mg-card-count">
          <Badge tone="neutral" variant="soft">
            {t('meetings.card.count', { count: group.meetingCount })}
          </Badge>
        </span>
        {canManage && onEdit && onDelete && (
          <span className="mg-card-acts">
            <Button
              icon="pi pi-pencil"
              text
              rounded
              aria-label={t('meetings.actions.edit')}
              disabled={busy}
              onClick={() => onEdit(group)}
            />
            <Button
              icon="pi pi-trash"
              text
              rounded
              severity="danger"
              aria-label={t('meetings.actions.delete')}
              disabled={busy}
              onClick={() => onDelete(group)}
            />
          </span>
        )}
      </div>

      {group.description && <p className="mg-card-desc">{group.description}</p>}

      {actionStatuses && actionStatuses.length > 0 && (
        <GroupActionStats
          total={group.actionTotal}
          counts={group.actionStatusCounts}
          statuses={actionStatuses}
        />
      )}
    </div>
  )
}
